import { Button } from '@heroui/react'
import { useUpdatePassword } from '@qse/auth-ui'
import { type ModalComponentProps, updatePasswordValidationSchema } from '@qse/constants'
import { FormikControl } from '@qse/ui'
import { Form, Formik } from 'formik'

interface UpdatePasswordFormValues {
  oldPassword: string
  newPassword: string
  confirmPassword: string
}

const initialValues: UpdatePasswordFormValues = {
  oldPassword: '',
  newPassword: '',
  confirmPassword: '',
}

export default function UpdatePassword(props: ModalComponentProps) {
  const { setModelActive, onClose } = props
  const { handleUpdatePassword } = useUpdatePassword()

  const handleSubmit = async (
    values: UpdatePasswordFormValues,
    {
      setSubmitting,
      resetForm,
      setStatus,
    }: {
      setSubmitting: (submitting: boolean) => void
      resetForm: () => void
      setStatus: (status: string) => void
    },
  ) => {
    try {
      await handleUpdatePassword(values, {
        setSubmitting,
        setStatus,
        onSuccess: () => {
          // Reset form and close modal on success
          resetForm()
          setModelActive(undefined)
          onClose()
        },
      })
    } catch (error) {
      console.error('Error updating password:', error)
      // Error handling is done in the hook
    }
  }

  const handleCancel = () => {
    setModelActive(undefined)
    onClose()
  }

  return (
    <div>
      <Formik
        initialValues={initialValues}
        validateOnBlur={true}
        validateOnChange={true}
        validationSchema={updatePasswordValidationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, isValid, dirty }) => (
          <Form className="flex flex-col gap-5 py-5 px-8">
            <div className="flex flex-col gap-2 justify-center items-center text-current">
              <p className="font-medium text-2xl">Update Your Password</p>
              <p className="text-sm font-medium text-center text-gray-600 dark:text-gray-400">
                For your account's security, please enter your current password and choose a new
                one.
              </p>
            </div>

            <FormikControl
              isRequired
              control="password"
              description="Enter the password you're currently using to log in"
              label="Current Password"
              name="oldPassword"
              placeholder="Enter your current password"
            />

            <FormikControl
              isRequired
              control="password"
              description="Must be at least 8 characters with uppercase, lowercase, number, and special character"
              label="New Password"
              name="newPassword"
              placeholder="Enter your new password"
            />

            <FormikControl
              isRequired
              control="password"
              description="Re-enter your new password to confirm"
              label="Confirm New Password"
              name="confirmPassword"
              placeholder="Confirm your new password"
            />

            <div className="flex justify-end gap-3">
              <Button disabled={isSubmitting} radius="sm" variant="flat" onPress={handleCancel}>
                Cancel
              </Button>
              <Button
                color="primary"
                isDisabled={!isValid || !dirty || isSubmitting}
                isLoading={isSubmitting}
                radius="sm"
                type="submit"
              >
                {isSubmitting ? 'Updating Password...' : 'Update Password'}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  )
}
